import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { setDefaultAuthErrorHandler } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, onLogout, onLogin }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const onLogoutRef = useRef(onLogout);
  const onLoginRef = useRef(onLogin);

  // Update refs when callbacks change
  useEffect(() => {
    onLogoutRef.current = onLogout;
    onLoginRef.current = onLogin;
  }, [onLogout, onLogin]);

  // Handle authentication errors (401/403) - redirect to login
  const handleAuthError = useRef(() => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setUser(null);
    
    // Call logout callback
    if (onLogoutRef.current) {
      onLogoutRef.current();
    }
    
    // Redirect to login page
    if (window.location.pathname !== '/signin') {
      window.location.href = '/signin';
    }
  });

  // Set default auth error handler for apiRequest
  useEffect(() => {
    setDefaultAuthErrorHandler(handleAuthError.current);
  }, []);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');
    if (token && userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        setUser({ token, ...parsedUserData });
        // Fetch projects if user is already logged in
        if (onLoginRef.current) {
          onLoginRef.current();
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        setUser({ token });
        if (onLoginRef.current) {
          onLoginRef.current();
        }
      }
    }
    setLoading(false);
  }, []); // Remove onLogin from dependencies to prevent infinite loop

  const login = (token, userData = {}) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    setUser({ token, ...userData });
    // Fetch projects after successful login
    if (onLoginRef.current) {
      onLoginRef.current();
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setUser(null);
    // Call additional logout callback if provided
    if (onLogoutRef.current) {
      onLogoutRef.current();
    }
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    loading,
    handleAuthError: handleAuthError.current
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
